import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  TrendingUp, Award, Users, RefreshCw, Loader2,
  ChevronUp, ChevronDown, BarChart3, User, Sliders,
  Download, Building2, List, Grid3X3,
} from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { YearContext } from '../components/Layout/Layout';
import StatCard from '../components/Cards/StatCard';
import { schoolConfigAPI, performanceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Constants ─────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const METRIC_LABELS = {
  attendance_score:        'Attendance',
  lesson_plan_score:       'Lesson Plan',
  learning_tracker_score:  'Learning Tracker',
  notebook_score:          'Notebook',
  gradebook_score:         'Gradebook',
  homework_score:          'Homework',
  observation_score:       'Observation',
  newsletter_score:        'Newsletter',
  events_led_score:        'Events Led',
};

// ── HR Matrix constants ────────────────────────────────────────
const HR_METRICS = [
  { key: 'attendance_score',        label: 'Attendance',     weight: 20 },
  { key: 'lesson_plan_score',       label: 'Lesson Plan',    weight: 15 },
  { key: 'learning_tracker_score',  label: 'Lrn Tracker',   weight: 10 },
  { key: 'notebook_score',          label: 'Notebook',       weight:  5, manual: true },
  { key: 'gradebook_score',         label: 'Gradebook',      weight: 15, manual: true },
  { key: 'homework_score',          label: 'Homework',       weight:  5 },
  { key: 'observation_score',       label: 'Observation',    weight: 20 },
  { key: 'newsletter_score',        label: 'Newsletter',     weight:  5 },
  { key: 'events_led_score',        label: 'Events Led',     weight:  5, manual: true },
];

const MATRIX_COLOR = s =>
  s == null ? '' :
  s >= 90   ? 'bg-emerald-100 text-emerald-800' :
  s >= 70   ? 'bg-amber-100 text-amber-800' :
               'bg-red-100 text-red-700';
const SCORE_COLOR = (s) =>
  s == null ? 'text-slate-400' :
  s >= 80   ? 'text-emerald-600' :
  s >= 60   ? 'text-amber-600'   : 'text-red-500';
const SCORE_BG = (s) =>
  s == null ? 'bg-slate-100' :
  s >= 80   ? 'bg-emerald-100' :
  s >= 60   ? 'bg-amber-100'   : 'bg-red-100';

const ttStyle = {
  backgroundColor: 'rgba(255,255,255,0.97)',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  fontSize: 12,
};

const DEPT_COLORS = ['#0ea5e9','#10b981','#8b5cf6','#f59e0b','#f43f5e','#06b6d4','#84cc16','#ec4899'];

// ── Score bar ─────────────────────────────────────────────────
function ScoreBar({ score }) {
  const pct = Math.min(Math.max(score ?? 0, 0), 100);
  const bg  = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${bg} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold w-10 text-right ${SCORE_COLOR(score)}`}>
        {score != null ? `${score}%` : '—'}
      </span>
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────
function OverviewTab({ yearId, month, year }) {
  const [lb,      setLb]      = useState(null);
  const [trend,   setTrend]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lbRes, trendRes] = await Promise.all([
        performanceAPI.getLeaderboard({ academicYearId: yearId, month, year, topN: 10 }),
        performanceAPI.getScores({ academicYearId: yearId, year, limit: 500 }),
      ]);
      setLb(lbRes.data);
      const byMonth = {};
      for (const row of trendRes.data.data || []) {
        const m = row.month_number;
        if (!byMonth[m]) byMonth[m] = { month: MONTHS[m-1], total: 0, count: 0 };
        if (row.total_score != null) { byMonth[m].total += row.total_score; byMonth[m].count++; }
      }
      setTrend(
        Object.values(byMonth)
          .map(r => ({ month: r.month, avg: r.count ? Math.round(r.total / r.count * 10) / 10 : null }))
          .filter(r => r.avg != null)
      );
    } catch { toast.error('Failed to load overview'); }
    finally { setLoading(false); }
  }, [yearId, month, year]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500" /></div>;
  if (!lb) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Avg Score" value={lb.avg_score != null ? `${lb.avg_score}%` : '—'} icon={TrendingUp} color="blue" subtitle={`${MONTHS[month-1]} ${year}`} />
        <StatCard title="Employees Scored" value={lb.total_employees ?? 0} icon={Users} color="green" />
        <StatCard title="Top Performer" value={lb.top?.[0]?.employee_name?.split(' ')[0] ?? '—'} icon={Award} color="purple" subtitle={lb.top?.[0]?.total_score != null ? `${lb.top[0].total_score}%` : ''} />
        <StatCard title="Needs Support" value={lb.bottom?.[0]?.employee_name?.split(' ')[0] ?? '—'} icon={Users} color="yellow" subtitle={lb.bottom?.[0]?.total_score != null ? `${lb.bottom[0].total_score}%` : ''} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-5">
          <h3 className="font-display font-semibold text-slate-800 text-sm mb-4">Monthly Avg Score Trend</h3>
          {trend.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No data yet — run Compute to generate scores</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={ttStyle} formatter={(v) => [`${v}%`, 'Avg Score']} />
                <Line type="monotone" dataKey="avg" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4, fill: '#0ea5e9' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="glass-card p-5">
          <h3 className="font-display font-semibold text-slate-800 text-sm mb-4">Top 10 Performers — {MONTHS[month-1]}</h3>
          {!lb.top?.length ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No scores for this month</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={lb.top.map(r => ({ name: r.employee_name.split(' ')[0], score: r.total_score }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={70} />
                <Tooltip contentStyle={ttStyle} formatter={(v) => [`${v}%`, 'Score']} />
                <Bar dataKey="score" radius={[0, 6, 6, 0]} maxBarSize={18}>
                  {lb.top.map((_, i) => <Cell key={i} fill={i === 0 ? '#8b5cf6' : '#0ea5e9'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[{ label: 'Top Performers', data: lb.top, icon: ChevronUp, color: 'emerald' },
          { label: 'Needs Attention', data: lb.bottom, icon: ChevronDown, color: 'red' }].map(({ label, data, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
              <Icon size={14} className={`text-${color}-500`} />
              <h4 className="text-sm font-semibold text-slate-700">{label}</h4>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-50">
                {(data || []).map((r, i) => (
                  <tr key={r.employee_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 w-8 text-center text-xs font-bold text-slate-400">#{i+1}</td>
                    <td className="px-2 py-2.5 font-medium text-slate-700 truncate max-w-[140px]">{r.employee_name}</td>
                    <td className="px-2 py-2.5 text-xs text-slate-400 truncate">{r.department || '—'}</td>
                    <td className="px-4 py-2.5"><ScoreBar score={r.total_score} /></td>
                  </tr>
                ))}
                {!data?.length && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400 text-xs">No data</td></tr>}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Employee detail tab ────────────────────────────────────────
function EmployeeTab({ yearId }) {
  const [employees, setEmployees] = useState([]);
  const [selected,  setSelected]  = useState('');
  const [detail,    setDetail]    = useState(null);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    schoolConfigAPI.getStaff().then(r => setEmployees((r.data || []).filter(e => e.is_active))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    performanceAPI.getEmployee(selected, { academicYearId: yearId })
      .then(r => setDetail(r.data))
      .catch(() => toast.error('Failed to load employee data'))
      .finally(() => setLoading(false));
  }, [selected, yearId]);

  const radarData = detail?.scores?.length
    ? Object.entries(METRIC_LABELS).map(([key, name]) => ({
        metric: name,
        value: detail.scores[detail.scores.length - 1][key] ?? 0,
      }))
    : [];

  const trendData = (detail?.scores || []).map(s => ({
    month: MONTHS[s.month_number - 1],
    total: s.total_score,
  }));

  return (
    <div className="space-y-5">
      <select
        value={selected}
        onChange={e => setSelected(e.target.value)}
        className="w-full md:w-80 border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
      >
        <option value="">— Select an employee —</option>
        {employees.map(e => <option key={e.id} value={e.id}>{e.name}{e.designation ? ` (${e.designation})` : ''}</option>)}
      </select>

      {loading && <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-primary-500" /></div>}

      {detail && !loading && (
        <div className="space-y-5">
          <div className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <User size={22} className="text-primary-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-slate-800">{detail.employee.name}</div>
              <div className="text-xs text-slate-500">{detail.employee.designation} · {detail.employee.department}</div>
            </div>
            {detail.rank && (
              <div className="text-right">
                <div className="text-2xl font-display font-bold text-primary-600">#{detail.rank}</div>
                <div className="text-xs text-slate-500">School rank</div>
              </div>
            )}
          </div>

          {!detail.scores?.length ? (
            <div className="text-center py-10 text-slate-400 text-sm">No performance data yet — run Compute first</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="glass-card p-5">
                <h3 className="font-display font-semibold text-slate-700 text-sm mb-4">Total Score by Month</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={ttStyle} formatter={(v) => v != null ? [`${v}%`] : ['—']} />
                    <Line type="monotone" dataKey="total" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4, fill: '#0ea5e9' }} activeDot={{ r: 6 }} name="Total" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="glass-card p-5">
                <h3 className="font-display font-semibold text-slate-700 text-sm mb-4">
                  Latest Month Breakdown ({MONTHS[(detail.scores[detail.scores.length-1].month_number)-1]})
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} />
                    <Radar name="Score" dataKey="value" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.25} strokeWidth={2} />
                    <Tooltip contentStyle={ttStyle} formatter={(v) => [`${v != null ? v : 0}%`]} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {detail.scores?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Month</th>
                    {Object.values(METRIC_LABELS).map(l => <th key={l} className="px-3 py-2.5 text-right">{l}</th>)}
                    <th className="px-4 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {detail.scores.map(s => (
                    <tr key={`${s.month_number}-${s.year_number}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-700">{MONTHS[s.month_number-1]} {s.year_number}</td>
                      {Object.keys(METRIC_LABELS).map(k => (
                        <td key={k} className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(s[k])}`}>
                          {s[k] != null ? `${s[k]}%` : '—'}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-right">
                        <span className={`text-sm font-bold ${SCORE_COLOR(s.total_score)}`}>
                          {s.total_score != null ? `${s.total_score}%` : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── All Scores tab ─────────────────────────────────────────────
function AllScoresTab({ yearId, month, year }) {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('total_score');
  const [sortDir,   setSortDir]   = useState('desc');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await performanceAPI.getScores({ academicYearId: yearId, month, year, limit: 500 });
      setRows(r.data.data || []);
    } catch { toast.error('Failed to load scores'); }
    finally { setLoading(false); }
  }, [yearId, month, year]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = rows
    .filter(r => !search ||
      r.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.department?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const av = a[sortKey] ?? -1, bv = b[sortKey] ?? -1;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await performanceAPI.export({ academicYearId: yearId, month, year });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance_${MONTHS[month-1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const SortIcon = ({ col }) => (
    <span className="ml-0.5 opacity-50 hover:opacity-100 cursor-pointer select-none" onClick={() => handleSort(col)}>
      {sortKey === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
    </span>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name or department..."
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
        <button
          onClick={handleExport}
          disabled={exporting || !rows.length}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export Excel
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2.5 text-center w-10">#</th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Dept</th>
                {Object.entries(METRIC_LABELS).map(([k, l]) => (
                  <th key={k} className="px-3 py-2.5 text-right cursor-pointer whitespace-nowrap" onClick={() => handleSort(k)}>
                    {l}<SortIcon col={k} />
                  </th>
                ))}
                <th className="px-3 py-2.5 text-right cursor-pointer" onClick={() => handleSort('total_score')}>
                  Total<SortIcon col="total_score" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((r, i) => (
                <tr key={r.id || r.employee_id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 text-xs text-slate-400 text-center">{i+1}</td>
                  <td className="px-3 py-2.5 font-medium text-slate-700 max-w-[160px] truncate">{r.employee_name}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-400 max-w-[110px] truncate">{r.department || '—'}</td>
                  {Object.keys(METRIC_LABELS).map(k => (
                    <td key={k} className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(r[k])}`}>
                      {r[k] != null ? `${r[k]}%` : '—'}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right"><ScoreBar score={r.total_score} /></td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400 text-sm">
                  {rows.length ? 'No results match your search' : 'No scores for this month — run Compute first'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {filtered.length > 0 && (
        <p className="text-xs text-slate-400 text-right">{filtered.length} employee{filtered.length !== 1 ? 's' : ''}</p>
      )}
    </div>
  );
}

// ── Department tab ─────────────────────────────────────────────
function DepartmentTab({ yearId, month, year }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await performanceAPI.getDeptSummary({ academicYearId: yearId, month, year });
      setData(r.data.data || []);
    } catch { toast.error('Failed to load department data'); }
    finally { setLoading(false); }
  }, [yearId, month, year]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500" /></div>;
  if (!data.length) return (
    <div className="text-center py-16 text-slate-400">No department data yet — run Compute first</div>
  );

  return (
    <div className="space-y-6">
      <div className="glass-card p-5">
        <h3 className="font-display font-semibold text-slate-800 text-sm mb-4">
          Avg Score by Department — {MONTHS[month-1]} {year}
        </h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 0, right: 10, left: 0, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="department"
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={false} tickLine={false}
              angle={-30} textAnchor="end"
            />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={ttStyle} formatter={(v) => v != null ? [`${v}%`, 'Avg Score'] : ['—', 'Avg Score']} />
            <Bar dataKey="avg_score" radius={[6, 6, 0, 0]} maxBarSize={60}>
              {data.map((_, i) => <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
            <tr>
              <th className="px-4 py-2.5 text-left">Department</th>
              <th className="px-3 py-2.5 text-right">Staff</th>
              <th className="px-3 py-2.5 text-right">Avg Score</th>
              <th className="px-3 py-2.5 text-right">Attendance</th>
              <th className="px-3 py-2.5 text-right">Lesson Plan</th>
              <th className="px-3 py-2.5 text-right">Homework</th>
              <th className="px-3 py-2.5 text-right">Newsletter</th>
              <th className="px-3 py-2.5 text-right">Observation</th>
              <th className="px-3 py-2.5 text-right text-emerald-600">High ≥80%</th>
              <th className="px-3 py-2.5 text-right text-red-500">Needs &lt;60%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {data.map(d => (
              <tr key={d.department} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium text-slate-700">{d.department}</td>
                <td className="px-3 py-2.5 text-right text-slate-500">{d.employee_count}</td>
                <td className="px-3 py-2.5 text-right">
                  <span className={`font-bold ${SCORE_COLOR(d.avg_score)}`}>{d.avg_score ?? '—'}%</span>
                </td>
                <td className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(d.avg_attendance)}`}>{d.avg_attendance != null ? `${d.avg_attendance}%` : '—'}</td>
                <td className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(d.avg_lesson_plan)}`}>{d.avg_lesson_plan != null ? `${d.avg_lesson_plan}%` : '—'}</td>
                <td className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(d.avg_homework)}`}>{d.avg_homework != null ? `${d.avg_homework}%` : '—'}</td>
                <td className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(d.avg_newsletter)}`}>{d.avg_newsletter != null ? `${d.avg_newsletter}%` : '—'}</td>
                <td className={`px-3 py-2.5 text-right text-xs font-semibold ${SCORE_COLOR(d.avg_observation)}`}>{d.avg_observation != null ? `${d.avg_observation}%` : '—'}</td>
                <td className="px-3 py-2.5 text-right text-xs font-bold text-emerald-600">{d.high_performers}</td>
                <td className="px-3 py-2.5 text-right text-xs font-bold text-red-500">{d.needs_attention}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── My Performance tab ─────────────────────────────────────────
function MyPerformanceTab({ yearId }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    performanceAPI.getMyScore({ academicYearId: yearId })
      .then(r => { if (r.data?.employee) setDetail(r.data); })
      .catch(() => toast.error('Could not load your performance data'))
      .finally(() => setLoading(false));
  }, [yearId]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500" /></div>;
  if (!detail) return (
    <div className="text-center py-16">
      <User size={48} className="mx-auto text-slate-300 mb-3" />
      <p className="text-slate-600 font-medium">No employee record linked to your account</p>
      <p className="text-slate-400 text-sm mt-1">Ask your admin to ensure your email matches your staff record.</p>
    </div>
  );

  const latest   = detail.scores?.[detail.scores.length - 1];
  const trendData = (detail.scores || []).map(s => ({ month: MONTHS[s.month_number-1], score: s.total_score }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4 bg-white rounded-xl border border-slate-200 p-5">
        <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <User size={26} className="text-primary-600" />
        </div>
        <div className="flex-1">
          <div className="text-lg font-display font-semibold text-slate-800">{detail.employee.name}</div>
          <div className="text-sm text-slate-500">{detail.employee.designation} · {detail.employee.department}</div>
        </div>
        {detail.rank && (
          <div className="text-center bg-primary-50 rounded-xl px-5 py-3 border border-primary-100">
            <div className="text-3xl font-display font-bold text-primary-600">#{detail.rank}</div>
            <div className="text-xs text-slate-500 mt-0.5">School rank</div>
          </div>
        )}
      </div>

      {!detail.scores?.length ? (
        <div className="text-center py-10 text-slate-400 text-sm bg-slate-50 rounded-xl border border-slate-100">
          No performance data yet — scores are computed nightly.
        </div>
      ) : (
        <>
          {latest && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(METRIC_LABELS).map(([key, label]) => (
                <div key={key} className={`rounded-xl p-4 border ${SCORE_BG(latest[key])} border-white/60 shadow-sm`}>
                  <div className={`text-2xl font-display font-bold ${SCORE_COLOR(latest[key])}`}>
                    {latest[key] != null ? `${latest[key]}%` : '—'}
                  </div>
                  <div className="text-xs font-medium text-slate-600 mt-1">{label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass-card p-5">
              <h3 className="font-display font-semibold text-slate-700 text-sm mb-4">My Score Over Time</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={ttStyle} formatter={(v) => v != null ? [`${v}%`, 'Score'] : ['—', 'Score']} />
                  <Line type="monotone" dataKey="score" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4, fill: '#0ea5e9' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {latest && (
              <div className="glass-card p-5">
                <h3 className="font-display font-semibold text-slate-700 text-sm mb-4">
                  Breakdown — {MONTHS[latest.month_number-1]} {latest.year_number}
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={Object.entries(METRIC_LABELS).map(([k, name]) => ({ metric: name, value: latest[k] ?? 0 }))}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} />
                    <Radar name="Score" dataKey="value" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.25} strokeWidth={2} />
                    <Tooltip contentStyle={ttStyle} formatter={(v) => [`${v}%`]} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── HR Matrix tab ─────────────────────────────────────────────
function HRMatrixTab({ yearId, month, year, isAdmin }) {
  const [data,    setData]    = useState([]);
  const [average, setAverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await performanceAPI.getHRMatrix({ academicYearId: yearId, month, year });
      setData(r.data.data);
      setAverage(r.data.average);
    } catch { toast.error('Failed to load HR Matrix'); }
    finally { setLoading(false); }
  }, [yearId, month, year]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await performanceAPI.updateManualScore(editing.employeeId, {
        academicYearId: yearId, month, year,
        metric: editing.metric.replace('_score', ''),
        score: editing.value === '' ? null : parseFloat(editing.value),
      });
      toast.success('Score saved');
      setEditing(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const r = await performanceAPI.export({ academicYearId: yearId, month, year });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hr_matrix_${MONTHS[month - 1]}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          HR Performance Matrix — {MONTHS[month - 1]} {year}
          {isAdmin && (
            <span className="ml-2 text-xs text-primary-600 bg-primary-50 border border-primary-100 px-2 py-0.5 rounded-full">
              Click Notebook / Gradebook / Events Led cells to enter scores
            </span>
          )}
        </p>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700"
        >
          <Download size={14} /> Export Excel
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-slate-800 text-white">
              <th className="text-left px-4 py-3 font-semibold sticky left-0 bg-slate-800 min-w-[180px]">Teacher</th>
              {HR_METRICS.map(m => (
                <th key={m.key} className={`px-2 py-2 text-center font-semibold min-w-[80px] ${m.manual ? 'bg-slate-700' : ''}`}>
                  <div>{m.label}</div>
                  <div className="text-slate-400 text-[10px] font-normal mt-0.5">{m.weight}%</div>
                </th>
              ))}
              <th className="px-3 py-3 text-center font-semibold min-w-[76px] bg-slate-700">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={HR_METRICS.length + 2} className="text-center py-12 text-slate-400">
                  No data for {MONTHS[month - 1]} {year}. Run Compute in Settings first.
                </td>
              </tr>
            ) : data.map((row, idx) => (
              <tr key={row.employee_id}
                className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                <td className="px-4 py-2.5 sticky left-0 bg-inherit font-medium text-slate-800">
                  <div className="leading-tight">{row.employee_name}</div>
                  {row.designation && <div className="text-[10px] text-slate-400 mt-0.5">{row.designation}</div>}
                </td>
                {HR_METRICS.map(m => {
                  const val = row[m.key];
                  const canEdit = isAdmin && m.manual;
                  return (
                    <td
                      key={m.key}
                      className={`px-2 py-2.5 text-center ${canEdit ? 'cursor-pointer' : ''}`}
                      onClick={canEdit ? () => setEditing({
                        employeeId: row.employee_id,
                        name: row.employee_name,
                        metric: m.key,
                        metricLabel: m.label,
                        value: val != null ? String(val) : '',
                      }) : undefined}
                    >
                      {val != null ? (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${MATRIX_COLOR(val)}`}>
                          {val}%
                        </span>
                      ) : canEdit ? (
                        <span className="text-primary-400 text-[10px] hover:text-primary-600">+ Add</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-2.5 text-center">
                  {row.total_score != null ? (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${MATRIX_COLOR(row.total_score)}`}>
                      {row.total_score}%
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
          {average && data.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-100">
                <td className="px-4 py-2.5 sticky left-0 bg-slate-100 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  Average
                </td>
                {HR_METRICS.map(m => (
                  <td key={m.key} className="px-2 py-2.5 text-center">
                    {average[m.key] != null ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${MATRIX_COLOR(average[m.key])}`}>
                        {average[m.key]}%
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                ))}
                <td className="px-2 py-2.5 text-center">
                  {average.total_score != null ? (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${MATRIX_COLOR(average.total_score)}`}>
                      {average.total_score}%
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-200 inline-block" /> ≥ 90%</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-200 inline-block" /> 70–89%</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-200 inline-block" /> &lt; 70%</span>
        {isAdmin && <span className="text-slate-400 ml-2">Darker header columns (Notebook, Gradebook, Events Led) are manually entered.</span>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-80" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-1 text-sm">{editing.metricLabel} Score</h3>
            <p className="text-xs text-slate-500 mb-4">{editing.name}</p>
            <input
              type="number" min="0" max="100" step="0.5"
              value={editing.value}
              onChange={e => setEditing(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Enter score (0–100)"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-400"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-primary-600 text-white rounded-xl py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {saving && <Loader2 size={13} className="animate-spin" />}
                Save
              </button>
              <button
                onClick={() => setEditing(null)}
                className="flex-1 bg-slate-100 text-slate-700 rounded-xl py-2 text-sm font-medium hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Settings tab ───────────────────────────────────────────────
function SettingsTab({ yearId }) {
  const [weights,   setWeights]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [computing, setComputing] = useState(false);
  const [compMonth, setCompMonth] = useState(new Date().getMonth() + 1);
  const [compYear,  setCompYear]  = useState(new Date().getFullYear());

  useEffect(() => {
    performanceAPI.getConfig({ academicYearId: yearId })
      .then(r => setWeights(r.data))
      .catch(() => toast.error('Failed to load config'))
      .finally(() => setLoading(false));
  }, [yearId]);

  const activeTotal = weights.filter(w => w.is_active).reduce((s, w) => s + parseFloat(w.weight || 0), 0);
  const totalOk = Math.abs(activeTotal - 100) <= 0.5;

  const handleSave = async () => {
    if (!totalOk) return toast.error(`Active weights must sum to 100 (currently ${activeTotal.toFixed(1)})`);
    setSaving(true);
    try {
      await performanceAPI.updateConfig({ academicYearId: yearId, metrics: weights });
      toast.success('Weights saved');
    } catch(e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleCompute = async () => {
    setComputing(true);
    try {
      const r = await performanceAPI.compute({ academicYearId: yearId, month: compMonth, year: compYear });
      toast.success(r.data.message);
    } catch(e) { toast.error(e.response?.data?.error || 'Compute failed'); }
    finally { setComputing(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={22} className="animate-spin text-primary-500" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-semibold text-slate-800">Metric Weights</h3>
          <span className={`text-sm font-semibold px-3 py-1 rounded-full ${totalOk ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            Total: {activeTotal.toFixed(1)}%
          </span>
        </div>
        <div className="space-y-4">
          {weights.map((w, i) => (
            <div key={w.metric} className="flex items-center gap-4">
              <input
                type="checkbox"
                checked={!!w.is_active}
                onChange={e => setWeights(prev => prev.map((p, j) => j === i ? { ...p, is_active: e.target.checked ? 1 : 0 } : p))}
                className="rounded"
              />
              <span className="w-44 text-sm text-slate-700">{w.label}</span>
              <input
                type="number" min="0" max="100" step="0.5"
                value={w.weight}
                disabled={!w.is_active}
                onChange={e => setWeights(prev => prev.map((p, j) => j === i ? { ...p, weight: e.target.value } : p))}
                className="w-24 border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-40 disabled:bg-slate-50"
              />
              <span className="text-sm text-slate-400">%</span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-primary-400 rounded-full transition-all" style={{ width: `${Math.min(parseFloat(w.weight || 0), 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !totalOk}
          className="mt-5 flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Save Weights
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-slate-800 mb-4">Manual Recompute</h3>
        <p className="text-sm text-slate-500 mb-4">
          Scores are computed nightly at 1:00 AM. Use this to trigger an immediate recompute for a specific month.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={compMonth}
            onChange={e => setCompMonth(parseInt(e.target.value))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <input
            type="number" value={compYear} min="2020" max="2030"
            onChange={e => setCompYear(parseInt(e.target.value))}
            className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <button
            onClick={handleCompute}
            disabled={computing}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            {computing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Compute Now
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
const MONTH_NAMES_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_PICKER_TABS = new Set(['overview', 'all', 'dept', 'hr_matrix']);

export default function PerformanceAnalytics() {
  const { user }  = useAuth();
  const { yearId } = useContext(YearContext);
  const role     = user?.role;
  const isAdmin  = ['admin', 'super_admin'].includes(role);
  const isSelf   = ['user', 'teacher'].includes(role);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());

  const tabs = [
    ...(isSelf   ? [{ key: 'mine',     label: 'My Performance',  icon: User }]       : []),
    ...(!isSelf  ? [{ key: 'overview', label: 'Overview',        icon: BarChart3 }]  : []),
    ...(!isSelf  ? [{ key: 'employee', label: 'By Employee',     icon: Users }]      : []),
    ...(!isSelf  ? [{ key: 'all',      label: 'All Scores',      icon: List }]       : []),
    ...(!isSelf  ? [{ key: 'dept',      label: 'By Department',   icon: Building2 }]  : []),
    ...(!isSelf  ? [{ key: 'hr_matrix', label: 'HR Matrix',      icon: Grid3X3 }]    : []),
    ...(isAdmin  ? [{ key: 'settings', label: 'Settings',        icon: Sliders }]    : []),
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]?.key || 'overview');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <TrendingUp size={24} className="text-primary-600" /> Performance Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Weighted performance scores across attendance, lesson plans, homework, newsletter, and observations
          </p>
        </div>
        {MONTH_PICKER_TABS.has(activeTab) && (
          <div className="flex items-center gap-2">
            <select
              value={month}
              onChange={e => setMonth(parseInt(e.target.value))}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              {MONTH_NAMES_LONG.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <input
              type="number" value={year} min="2020" max="2030"
              onChange={e => setYear(parseInt(e.target.value))}
              className="w-24 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 bg-slate-100 p-1.5 rounded-2xl w-fit">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition whitespace-nowrap
              ${activeTab === key ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'}`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'overview' && <OverviewTab    yearId={yearId} month={month} year={year} />}
        {activeTab === 'employee' && <EmployeeTab    yearId={yearId} />}
        {activeTab === 'all'      && <AllScoresTab   yearId={yearId} month={month} year={year} />}
        {activeTab === 'dept'      && <DepartmentTab   yearId={yearId} month={month} year={year} />}
        {activeTab === 'hr_matrix' && <HRMatrixTab    yearId={yearId} month={month} year={year} isAdmin={isAdmin} />}
        {activeTab === 'mine'      && <MyPerformanceTab yearId={yearId} />}
        {activeTab === 'settings'  && <SettingsTab    yearId={yearId} />}
      </div>
    </div>
  );
}
